import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import {
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
} from "../Helper/formbtnhelper";
import NewEmail from "../Inputs/NewEmail";
import NewPhone from "../Inputs/NewPhone";
import NewSelect from "../Inputs/NewSelect";
import NewText from "../Inputs/NewText";
import NewTitle from "../Inputs/NewTitle";
import RadioGroupField from "../Inputs/RadioGroupField";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow";

// Slide 8: General Contractor/Operator Information
const GCInformation = ({ setSlideStatus, slideState, formStateLoaded }) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const {
    slide3,
    slide6,
    setSlide6,
    setIsLoading,
    requestStatus,
    names,
    phoneNumbers,
    emails,
    titles,
    companies,
    predictiveTextFetched,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
  } = useContext(FormContext);

  const { client, clientId, projectId } = useContext(DashboardContext);

  // ^ On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide6);
  }, []);

  // : MAIN QUERY LOGIC :
  const slide6Mutation = useSetSlide(
    clientId,
    projectId,
    6,
    setIsLoading,
    setSlideStatus,
  );

  useEffect(() => {
    if (client && client.shortName === "Greystone") {
      let newState = {
        ...slide6,
        gcName: client.name,
        gcAdd: client.address ? client.address : "",
        gcState: "NV",
        gcCity: client.city ? client.city : "",
        gcZip: client.zip ? client.zip : "",
        gcPhone: client.phone ? client.phone : "",
        gcFax: client.fax ? client.fax : "",
        gcTaxId: client.taxId ? client.taxId : "",
        gcContactName: "Joy Broddle",
        gcContactTitle: "Division President",
        gcContactEmail: "joy.broddle@lennar.com",
        gcContactPhone: "702-736-9100",
        gcAuthorizedContactName: "Courtney Breen",
        gcAuthorizedContactTitle: "Division Environmental Manager",
        sameAsOwner: "",
      };

      console.log("Greystone GC Info Auto-Fill:", newState);
      setSlide6(newState);

      slide6Mutation.mutate(newState);
    }
  }, []);

  // : INVALID INPUTS LOGIC :

  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "6",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <h2 className="question-section">Company Information</h2>
          <p className="important-text">
            Please provide the following information about this projects primary
            Operator/General Contractor. <br />
            All fields should match exactly as registered when forming this
            entity.
          </p>
          <RadioGroupField
            idProp="sameAsOwner"
            label="Is the Owner also the Operator?"
            state={slide6}
            setState={setSlide6}
            val={slide6.sameAsOwner}
            debouncedSave={slide6Mutation}
            setIsLoading={setIsLoading}
            disabledInput={disabledInputCheck("sameAsOwner", disabledInputs)}
          />
          <ResponsiveRow>
            <NewText
              idProp="gcName"
              label="Company Name"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcName}
              debouncedSave={slide6Mutation}
              type="company"
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? companies : []}
              req={slide6.gcName === "" || slide6.gcName.length < 3}
              disabledInput={disabledInputCheck("gcName", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcName", disabledInputs) === false
                  ? getInputMessage("gcName", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcName", invalidInputsData, "6")}
            />
            <NewText
              idProp="gcTaxId"
              label="Tax ID # (TIN)"
              type="taxId"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcTaxId}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={slide6.gcTaxId === ""}
              disabledInput={disabledInputCheck("gcTaxId", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcTaxId", disabledInputs) === false
                  ? getInputMessage("gcTaxId", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcTaxId", invalidInputsData, "6")}
            />
            <NewPhone
              idProp="gcPhone"
              label="Office Phone Number"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcPhone}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={!isValidPhoneNumber(slide6.gcPhone, false)}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              disabledInput={disabledInputCheck("gcPhone", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcPhone", disabledInputs) === false
                  ? getInputMessage("gcPhone", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcPhone", invalidInputsData, "6")}
            />
            <NewText
              idProp="gcFax"
              label="Fax Number"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcFax}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              type="fax"
              req={slide6.gcFax === ""}
              disabledInput={disabledInputCheck("gcFax", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcFax", disabledInputs) === false
                  ? getInputMessage("gcFax", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcFax", invalidInputsData, "6")}
            />
          </ResponsiveRow>
          <ResponsiveRow>
            <NewText
              idProp="gcAdd"
              label="Office Address"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcAdd}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={slide6.gcAdd === "" || slide6.gcAdd.length < 6}
              disabledInput={disabledInputCheck("gcAdd", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcAdd", disabledInputs) === false
                  ? getInputMessage("gcAdd", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcAdd", invalidInputsData, "6")}
              type="address"
            />
            <NewText
              idProp="gcCity"
              label="City"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcCity}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={slide6.gcCity === "" || slide6.gcCity.length < 3}
              disabledInput={disabledInputCheck("gcCity", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcCity", disabledInputs) === false
                  ? getInputMessage("gcCity", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcCity", invalidInputsData, "6")}
              type="city"
            />
            <NewSelect
              idProp="gcState"
              label="State"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcState}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={slide6.gcState === ""}
              disabledInput={disabledInputCheck("gcState", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcState", disabledInputs) === false
                  ? getInputMessage("gcState", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcState", invalidInputsData, "6")}
              type="state"
            />
            <NewText
              idProp="gcZip"
              label="Zip"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcZip}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={slide6.gcZip === "" || slide6.gcZip.length !== 5}
              disabledInput={disabledInputCheck("gcZip", disabledInputs)}
              disabledInputMessage={
                disabledInputCheck("gcZip", disabledInputs) === false
                  ? getInputMessage("gcZip", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData("gcZip", invalidInputsData, "6")}
              type="zipCode"
            />
          </ResponsiveRow>
          <h2 className="question-section">Contact Information</h2>
          <p className="important-text">
            Please provide the following information for the main contact at the
            General Contractor/Operator
          </p>
          <ResponsiveRow type="default">
            <NewText
              idProp="gcContactName"
              label="First & Last Name"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcContactName}
              debouncedSave={slide6Mutation}
              type="name"
              setIsLoading={setIsLoading}
              req={!isValidName(slide6.gcContactName, false)}
              autoData={predictiveTextFetched ? names : []}
              disabledInput={disabledInputCheck(
                "gcContactName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("gcContactName", disabledInputs) === false
                  ? getInputMessage("gcContactName", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData(
                "gcContactName",
                invalidInputsData,
                "6",
              )}
            />
            <NewTitle
              idProp="gcContactTitle"
              label="Title"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcContactTitle}
              debouncedSave={slide6Mutation}
              type="title"
              setIsLoading={setIsLoading}
              req={
                slide6.gcContactTitle === "" || slide6.gcContactTitle.length < 3
              }
              autoData={predictiveTextFetched ? titles : []}
              disabledInput={disabledInputCheck(
                "gcContactTitle",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("gcContactTitle", disabledInputs) === false
                  ? getInputMessage("gcContactTitle", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData(
                "gcContactTitle",
                invalidInputsData,
                "6",
              )}
            />
            <NewPhone
              idProp="gcContactPhone"
              label="Phone Number"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcContactPhone}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={!isValidPhoneNumber(slide6.gcContactPhone, false)}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              disabledInput={disabledInputCheck(
                "gcContactPhone",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("gcContactPhone", disabledInputs) === false
                  ? getInputMessage("gcContactPhone", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData(
                "gcContactPhone",
                invalidInputsData,
                "6",
              )}
            />
            <NewEmail
              idProp="gcContactEmail"
              label="Email Address"
              state={slide6}
              setState={setSlide6}
              val={slide6.gcContactEmail}
              debouncedSave={slide6Mutation}
              setIsLoading={setIsLoading}
              req={!isValidEmail(slide6.gcContactEmail, false)}
              autoData={predictiveTextFetched ? emails : []}
              disabledInput={disabledInputCheck(
                "gcContactEmail",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("gcContactEmail", disabledInputs) === false
                  ? getInputMessage("gcContactEmail", invalidInputsData, "6")
                  : ""
              }
              invalidData={getInvalidData(
                "gcContactEmail",
                invalidInputsData,
                "6",
              )}
            />
          </ResponsiveRow>
          {slide3.permittee === "operator" && (
            <>
              <h2 className="question-section">
                Authorized Signer Information
              </h2>
              <p className="important-text">
                {
                  "Please provide the contact information of the individual who will be the authorized signer for permits & SWPPPs"
                }
              </p>
              <ResponsiveRow type="center">
                <NewText
                  idProp="gcAuthorizedContactName"
                  label="First & Last Name"
                  state={slide6}
                  setState={setSlide6}
                  val={slide6.gcAuthorizedContactName}
                  debouncedSave={slide6Mutation}
                  setIsLoading={setIsLoading}
                  autoData={predictiveTextFetched ? names : []}
                  type="name"
                  req={!isValidName(slide6.gcAuthorizedContactName, false)}
                  disabledInput={disabledInputCheck(
                    "gcAuthorizedContactName",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck(
                      "gcAuthorizedContactName",
                      disabledInputs,
                    ) === false
                      ? getInputMessage(
                          "gcAuthorizedContactName",
                          invalidInputsData,
                          "6",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "gcAuthorizedContactName",
                    invalidInputsData,
                    "6",
                  )}
                />
                <NewTitle
                  idProp="gcAuthorizedContactTitle"
                  label="Title"
                  state={slide6}
                  setState={setSlide6}
                  val={slide6.gcAuthorizedContactTitle}
                  debouncedSave={slide6Mutation}
                  setIsLoading={setIsLoading}
                  autoData={predictiveTextFetched ? titles : []}
                  req={slide6.gcAuthorizedContactTitle === ""}
                  disabledInput={disabledInputCheck(
                    "gcAuthorizedContactTitle",
                    disabledInputs,
                  )}
                  disabledInputMessage={
                    disabledInputCheck(
                      "gcAuthorizedContactTitle",
                      disabledInputs,
                    ) === false
                      ? getInputMessage(
                          "gcAuthorizedContactTitle",
                          invalidInputsData,
                          "6",
                        )
                      : ""
                  }
                  invalidData={getInvalidData(
                    "gcAuthorizedContactTitle",
                    invalidInputsData,
                    "6",
                  )}
                />
              </ResponsiveRow>
            </>
          )}
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default GCInformation;
